import React from 'react';
import { IAriServiceHotSpot, IAriServiceAssemblyInformation } from '../../../actions/DataServiceEntities.g';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

/**
 * Resource strings from definition file
 */
interface IAriHotspotImageResources {
    centerButtonText: string;
    resetButtonText: string;
    zoomInButtonText: string;
    zoomOutButtonText: string;
    hotspotUsageHelpText: string;
    assemblyOptionsPlaceholderText: string;
}

/**
 * Component props
 */
interface IAriHotspotImageProps {
    renderHeader(renderToolButtonRow: JSX.Element | null): JSX.Element;
    assemblyDetailState: IAriServiceAssemblyInformation | undefined;
    resources: IAriHotspotImageResources;
    baseClassName: string;
    ImageUrl?: string;
    HotSpots?: IAriServiceHotSpot[];
    Name?: string;
    transformRef: React.Ref<ReactZoomPanPinchRef>;
    goToPart(id: string, selectedPartIdName: string): void;
    tagIdPrefix: string;
    isMobile: boolean;
}

/**
 * Component to render the image with hotspots
 * @param props component props
 */
export const AriHotspotImage: React.FC<IAriHotspotImageProps> = props => {
    const {
        baseClassName,
        // ImageUrl,
        // HotSpots,
        // Name,
        transformRef,
        resources: {
            centerButtonText,
            zoomInButtonText,
            zoomOutButtonText,
            resetButtonText,
            hotspotUsageHelpText,
            assemblyOptionsPlaceholderText
        },
        goToPart,
        tagIdPrefix,
        isMobile,
        renderHeader,
        assemblyDetailState
    } = props;

    const [mapUsed, setMapUsed] = React.useState<boolean>(false);
    const [shouldRender, setShouldRender] = React.useState<boolean>(true);

    React.useEffect(() => {
        // console.log(`In timeout: ${shouldRender}`);
        setShouldRender(false);
        setTimeout(() => {
            setShouldRender(true);
        }, 50);
    }, [assemblyDetailState]);

    /**
     * Generates the style required for the hotspot
     * @param hs hotspot
     */
    const generateHotspotStyle = (hs: IAriServiceHotSpot): React.CSSProperties => {
        const cssProperties: React.CSSProperties = {};
        if (!hs.TopLeft?.X || !hs.TopLeft.Y) {
            return {};
        }
        cssProperties.left = `${hs.TopLeft.X}px`;
        cssProperties.top = `${hs.TopLeft.Y}px`;
        if (hs.BottomRight?.X && hs.BottomRight.Y) {
            cssProperties.height = `${parseInt(hs.BottomRight.X, 10) - parseInt(hs.TopLeft.X, 10) + 10}px`;
            cssProperties.width = `${parseInt(hs.BottomRight.Y, 10) - parseInt(hs.TopLeft.Y, 10) + 10}px`;
        }
        return cssProperties;
    };

    const renderToolButtonRow = (
        baseClassName: string,
        zoomIn: () => void,
        zoomOut: () => void,
        resetTransform: () => void,
        centerView: () => void,
        centerButtonText: string,
        zoomInButtonText: string,
        zoomOutButtonText: string,
        resetButtonText: string
    ) => {
        return (
            <div className={`${baseClassName}__tools ${isMobileToolsClassName}`}>
                <button className={`${baseClassName}__tools-button ${baseClassName}__tool-button--zoom-in`} onClick={() => zoomIn()}>
                    {zoomInButtonText}
                </button>
                <button className={`${baseClassName}__tools-button ${baseClassName}__tool-button--zoom-out`} onClick={() => zoomOut()}>
                    {zoomOutButtonText}
                </button>
                <button className={`${baseClassName}__tools-button ${baseClassName}__tool-button--reset`} onClick={() => resetTransform()}>
                    {resetButtonText}
                </button>
                <button className={`${baseClassName}__tools-button ${baseClassName}__tool-button--center`} onClick={() => centerView()}>
                    {centerButtonText}
                </button>
            </div>
        );
    };

    const handleHelperWrapperClick = () => setMapUsed(true);
    const initialScale = isMobile ? 0.4 : 0.6;
    const initialPositionX = isMobile ? 0 : 150;
    const isMobileToolsClassName = isMobile ? `${baseClassName}__tools--mobile` : '';
    const isMobileTransformWrapperClassName = isMobile ? `${baseClassName}__transform-wrapper--mobile` : '';
    const selectedPartIdName = `${baseClassName}__part-selection-popover`;
    return (
        <TransformWrapper
            initialPositionX={initialPositionX}
            initialScale={initialScale}
            limitToBounds={true}
            ref={transformRef}
            minScale={0}
            wheel={{ wheelDisabled: true }}
        >
            {({ zoomIn, zoomOut, resetTransform, centerView, ...rest }) => (
                <React.Fragment>
                    {assemblyDetailState && shouldRender ? (
                        <>
                            {renderHeader(
                                renderToolButtonRow(
                                    baseClassName,
                                    zoomIn,
                                    zoomOut,
                                    resetTransform,
                                    centerView,
                                    centerButtonText,
                                    zoomInButtonText,
                                    zoomOutButtonText,
                                    resetButtonText
                                )
                            )}
                            <TransformComponent wrapperClass={`${baseClassName}__transform-wrapper ${isMobileTransformWrapperClassName}`}>
                                <div className={`${baseClassName}__zoom-wrapper`}>
                                    {!mapUsed ? (
                                        <div className={`${baseClassName}__help-text-wrapper`} onClick={handleHelperWrapperClick}>
                                            <span className={`${baseClassName}__help-text`}>{hotspotUsageHelpText}</span>
                                        </div>
                                    ) : null}
                                    <img src={assemblyDetailState.ImageUrl} alt={assemblyDetailState.Name} />
                                    <div className={`${baseClassName}__zoom-wrapper--hotspots`}>
                                        {!isMobile &&
                                            assemblyDetailState.HotSpots &&
                                            assemblyDetailState.HotSpots.map(hs => (
                                                <span
                                                    key={`${assemblyDetailState?.AssemblyId}-${hs.Tag}`}
                                                    style={generateHotspotStyle(hs)}
                                                    className={`${baseClassName}__hotspot`}
                                                    onClick={() => goToPart(hs.Tag!, selectedPartIdName)}
                                                    id={`${tagIdPrefix}${hs.Tag}`}
                                                />
                                            ))}
                                    </div>
                                </div>
                            </TransformComponent>
                        </>
                    ) : (
                        <>
                            {renderHeader(null)}
                            <TransformComponent wrapperClass={`${baseClassName}__image-placeholder`}>
                                <div />
                            </TransformComponent>
                            <div className={`${baseClassName}__image-placeholder`}>
                                <span>{assemblyOptionsPlaceholderText}</span>
                            </div>
                        </>
                    )}
                </React.Fragment>
            )}
        </TransformWrapper>
    );
};
